import React, { useEffect, useState } from "react";
import { Card, Table, Input, Select, Modal, Form, Tooltip, Button, Row, Col, DatePicker, Tag, Upload, Alert } from "antd";
import { useNavigate } from "react-router-dom";
import { SearchOutlined, EyeOutlined, CheckOutlined, EditOutlined, ReadOutlined, DownloadOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import BlogDescriptionModal, { outlineHasContent } from "./BlogDescriptionModal";
import { buildCheckboxColumnFilter, distinctOptions } from "../../components/TableFilterDropdown";

const { TextArea } = Input;

const PRIORITY_CLASS = { Low: "priority-low", Medium: "priority-medium", High: "priority-high", Urgent: "priority-urgent" };
const STATUS_CLASS = {
  Pending: "gfx-status--neutral", Assigned: "gfx-status--info", "In Progress": "gfx-status--info",
  "Waiting for Review": "gfx-status--warn", "Revision Requested": "gfx-status--warn", Revised: "gfx-status--info",
  Approved: "gfx-status--success", Completed: "gfx-status--success", Overdue: "gfx-status--danger", Cancelled: "gfx-status--neutral",
};
const STATUS_OPTIONS = Object.keys(STATUS_CLASS);
const PRIORITY_OPTIONS = Object.keys(PRIORITY_CLASS);
// Same friendly mapping as the request detail page's hero pill — without it
// this table showed raw enum text ("Waiting for Review") while the detail
// page for the same request showed "Under Review".
const STAGE_LABEL = {
  Pending: "New", Assigned: "New", "In Progress": "In Progress",
  "Waiting for Review": "Under Review", "Revision Requested": "Change Requested", Revised: "Revised",
  Completed: "Completed", Overdue: "Overdue", Cancelled: "Cancelled",
};

function BlogWriteOversight() {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [rowFilter, setRowFilter] = useState({});
  const [workloadReport, setWorkloadReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [decisionModal, setDecisionModal] = useState(null); // { record, decision } | null
  const [decisionForm] = Form.useForm();
  const [decidingId, setDecidingId] = useState(null); // request _id with an in-flight decision
  const [exportingId, setExportingId] = useState(null); // member _id with an in-flight export download
  const [editingRecord, setEditingRecord] = useState(null); // request being edited, or null
  const [editForm] = Form.useForm();
  const [editSaving, setEditSaving] = useState(false);
  const [editingOriginalAssignee, setEditingOriginalAssignee] = useState(null);
  const [editFileList, setEditFileList] = useState([]);
  const assignedToWatch = Form.useWatch("assignedTo", editForm);
  const [editOutline, setEditOutline] = useState(undefined);
  const [descModalOpen, setDescModalOpen] = useState(false);

  // Surfaces the real error (e.g. "Permission denied" for an admin with no
  // Role assigned) instead of silently leaving the table empty with no
  // explanation — that silence is exactly what made a real permission gap
  // look like "no data" before this fix.
  const showFetchError = (err, label) => {
    ShowToast(err?.response?.data?.message || `Failed to load ${label}`, Severty.ERROR);
  };

  const fetchMembers = () => {
    request({
      url: apiPath.blogWriteTeamMembers,
      method: "GET",
      onSuccess: (data) => setMembers(data?.data?.members || []),
      onError: (err) => showFetchError(err, "Blog Write team members"),
    });
  };

  const fetchRequests = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.priority) params.append("priority", filters.priority);
    if (filters.assignedTo) params.append("assignedTo", filters.assignedTo);
    if (filters.search) params.append("search", filters.search);
    request({
      url: `${apiPath.blogWriteRequests}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setRequests(data?.data?.requests || []);
      },
      onError: (err) => {
        setLoading(false);
        showFetchError(err, "blog write requests");
      },
    });
  };

  const fetchWorkloadReport = () => {
    request({
      url: apiPath.blogWriteWorkloadReport,
      method: "GET",
      onSuccess: (data) => setWorkloadReport(data?.data?.report || []),
      onError: (err) => showFetchError(err, "workload report"),
    });
  };

  useEffect(() => {
    fetchMembers();
    fetchWorkloadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const sendDecision = (record, decision, comment) => {
    setDecidingId(record._id);
    request({
      url: apiPath.blogWriteRequestReviewDecision(record._id),
      method: "PUT",
      data: { decision, comment },
      onSuccess: (data) => {
        setDecidingId(null);
        if (data.status === 200) {
          ShowToast(
            decision === "approved" ? "Submission approved" : "Revisions requested",
            Severty.SUCCESS
          );
          setDecisionModal(null);
          decisionForm.resetFields();
          fetchRequests();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: () => {
        setDecidingId(null);
        ShowToast("Failed to record decision", Severty.ERROR);
      },
    });
  };

  // Approving needs no feedback — only revision requests open the comment modal.
  const submitDecision = (values) =>
    sendDecision(decisionModal.record, "revision_requested", values.comment);

  const deleteRequest = (record) => {
    Modal.confirm({
      title: "Delete this blog write request?",
      content: `"${record.projectTitle}" will be removed and taken off the Blog Writer's queue. This can't be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve, reject) => {
          request({
            url: apiPath.blogWriteRequest(record._id),
            method: "DELETE",
            onSuccess: (data) => {
              if (data.status === 200) {
                ShowToast("Blog write request deleted", Severty.SUCCESS);
                fetchRequests();
                resolve();
              } else {
                ShowToast(data.message, Severty.ERROR);
                reject();
              }
            },
            onError: (err) => {
              ShowToast(err?.response?.data?.message || "Failed to delete request", Severty.ERROR);
              reject();
            },
          });
        }),
    });
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    setEditingOriginalAssignee(record.assignedTo?._id || null);
    editForm.setFieldsValue({
      clientNameRef: record.clientNameRef,
      projectTitle: record.projectTitle,
      deliveryDate: record.deliveryDate ? moment(record.deliveryDate) : null,
      priority: record.priority,
      assignedTo: record.assignedTo?._id || undefined,
      reassignReason: undefined,
    });
    setEditOutline(record.outline);
    setEditFileList([]);
  };

  const finishEditDetails = (values) => {
    const payload = { ...values };
    delete payload.assignedTo;
    delete payload.reassignReason;
    if (payload.deliveryDate) {
      payload.deliveryDate = `${payload.deliveryDate.format("YYYY-MM-DD")}T00:00:00.000Z`;
    }
    // The details endpoint now also accepts new reference files, so it has
    // to be multipart the same way create's POST is — a plain JSON body
    // would hang on the server's formidable parser.
    const formData = new FormData();
    Object.entries(payload).forEach(([key, val]) => {
      if (val !== undefined && val !== null) formData.append(key, val);
    });
    // outline is a nested object (headers/subheaders included) — JSON-encode
    // it onto the multipart body the same way this codebase already does for
    // other structured fields sent alongside file uploads (e.g. pollData in
    // AccessModuleController).
    formData.append("outline", JSON.stringify(editOutline || {}));
    editFileList.forEach((f) => formData.append("referenceFiles", f));
    request({
      url: apiPath.blogWriteRequestUpdate(editingRecord._id),
      method: "PUT",
      data: formData,
      onSuccess: (data) => {
        setEditSaving(false);
        if (data.status === 200) {
          ShowToast("Blog write request updated successfully", Severty.SUCCESS);
          setEditingRecord(null);
          setEditingOriginalAssignee(null);
          setEditFileList([]);
          setEditOutline(undefined);
          fetchRequests();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setEditSaving(false);
        ShowToast(err?.response?.data?.message || "Failed to update request", Severty.ERROR);
      },
    });
  };

  // Admin always overrides the workload block (per spec), so reassignment
  // here just needs the short-reason gate, no blocked-modal detour.
  const submitEdit = (values) => {
    setEditSaving(true);
    const reassigning = values.assignedTo !== editingOriginalAssignee;
    if (!reassigning) return finishEditDetails(values);

    request({
      url: apiPath.blogWriteOverrideAssign(editingRecord._id),
      method: "PUT",
      data: { assignedTo: values.assignedTo, reason: values.reassignReason, overrideReason: values.reassignReason },
      onSuccess: (data) => {
        if (data.status === 200) {
          finishEditDetails(values);
        } else {
          setEditSaving(false);
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setEditSaving(false);
        ShowToast(err?.response?.data?.message || "Failed to reassign request", Severty.ERROR);
      },
    });
  };

  const exportMemberWorkload = async (member) => {
    setExportingId(member.assignedTo);
    try {
      const token = localStorage.getItem("admin_token") || "";
      const url = `${apiPath.baseURL}/${apiPath.blogWriteWorkloadReportExport(member.assignedTo)}`;
      const response = await axios.get(url, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `BlogWrite_Workload_${member.name.replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      ShowToast("Failed to export workload report", Severty.ERROR);
    } finally {
      setExportingId(null);
    }
  };

  const columns = [
    { title: "Project Title", dataIndex: "projectTitle", key: "projectTitle", width: 180 },
    {
      title: "Client (internal)", dataIndex: "clientNameRef", key: "clientNameRef", width: 150,
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "clientNameRef",
        getOptions: (rows) => distinctOptions(rows, (r) => r.clientNameRef),
        getValue: (r) => r.clientNameRef,
      }),
    },
    {
      title: "Assigned By", key: "createdBy", width: 150, render: (_, r) => r.createdByName || r.createdBy?.name || "-",
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "createdBy",
        getOptions: (rows) => distinctOptions(rows, (r) => r.createdByName || r.createdBy?.name),
        getValue: (r) => r.createdByName || r.createdBy?.name,
      }),
    },
    {
      title: "Assigned To", dataIndex: "assignedTo", key: "assignedTo", width: 150, render: (a) => a?.name || "Unassigned",
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "assignedTo",
        getOptions: (rows) => distinctOptions(rows, (r) => r.assignedTo?.name || "Unassigned"),
        getValue: (r) => r.assignedTo?.name || "Unassigned",
      }),
    },
    {
      title: "Delivery Date", dataIndex: "deliveryDate", key: "deliveryDate", width: 140,
      render: (d) => (d ? moment(d).format("MMM D, YYYY") : "-"),
    },
    {
      title: "Priority", dataIndex: "priority", key: "priority", width: 110,
      render: (p) => (
        <span className="gfx-priority-pill-sm">
          <span className={`gfx-priority-dot ${PRIORITY_CLASS[p] || "priority-medium"}`} />
          {p}
        </span>
      ),
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "priority",
        getOptions: () => ["Low", "Medium", "High", "Urgent"].map((v) => ({ label: v, value: v })),
        getValue: (r) => r.priority,
      }),
    },
    {
      title: "Status", dataIndex: "status", key: "status", width: 150,
      render: (s, record) => (
        <span>
          <span className={`gfx-status-pill ${STATUS_CLASS[s] || "gfx-status--neutral"}`}>{STAGE_LABEL[s] || s}</span>
          {record.extensionRequest?.status === "pending" && (
            <Tag color="gold" style={{ marginLeft: 6 }}>Extension Pending</Tag>
          )}
        </span>
      ),
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "status",
        getOptions: (rows) => {
          const seen = new Map();
          rows.forEach((r) => {
            if (r.status && !seen.has(r.status)) seen.set(r.status, STAGE_LABEL[r.status] || r.status);
          });
          return [...seen.entries()]
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([value, label]) => ({ label, value }));
        },
        getValue: (r) => r.status,
      }),
    },
    {
      title: "Actions",
      key: "actions",
      width: 230,
      render: (_, record) => (
        <div className="row-actions">
          <Tooltip title="View">
            <button
              className="row-action-btn row-action-view"
              onClick={() => navigate(`/blog-write-requests/${record._id}`)}
            >
              <EyeOutlined />
            </button>
          </Tooltip>
          {record.status === "Waiting for Review" && (
            <>
              <Tooltip title="Approve">
                <button
                  className="row-action-btn row-action-edit"
                  disabled={decidingId === record._id}
                  onClick={() => sendDecision(record, "approved")}
                >
                  <CheckOutlined />
                </button>
              </Tooltip>
              <Tooltip title="Request Revisions">
                <button
                  className="row-action-btn row-action-contract"
                  disabled={decidingId === record._id}
                  onClick={() => setDecisionModal({ record, decision: "revision_requested" })}
                >
                  <EditOutlined />
                </button>
              </Tooltip>
            </>
          )}
          <Tooltip title="Edit">
            <button
              className="row-action-btn row-action-contract"
              onClick={() => openEditModal(record)}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              className="row-action-btn row-action-delete"
              onClick={() => deleteRequest(record)}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const workloadColumns = [
    { title: "Blog Writer", dataIndex: "name", key: "name" },
    { title: "Active Requests", dataIndex: "count", key: "count" },
    {
      title: "Export",
      key: "export",
      width: 160,
      render: (_, record) => (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={exportingId === record.assignedTo}
          onClick={() => exportMemberWorkload(record)}
        >
          Download Report
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="gfx-req-hero gfx-oversight-hero">
        <div className="gfx-req-hero-top">
          <div>
            <div className="gfx-req-hero-eyebrow">Blog Write Team</div>
            <h2 className="gfx-req-hero-title">Requests Oversight</h2>
          </div>
          <div className="gfx-hero-done">
            <div className="gfx-hero-done-ring">
              <ReadOutlined />
            </div>
            <div>
              <div className="gfx-hero-done-value">{requests.length}</div>
              <div className="gfx-hero-done-label">Total Requests</div>
            </div>
          </div>
        </div>
      </div>

      <Card bordered={false} className="criclebox mb-24 gfx-oversight-toolbar-card">
        <div className="gfx-oversight-toolbar">
          <Input
            allowClear
            placeholder="Search client / project"
            prefix={<SearchOutlined style={{ color: "#94A3B8" }} />}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value || undefined }))}
            className="gfx-oversight-search"
          />
          <Select
            allowClear
            placeholder="Blog Writer"
            className="gfx-oversight-select"
            onChange={(v) => setFilters((f) => ({ ...f, assignedTo: v }))}
            options={members.map((m) => ({ label: m.name, value: m._id }))}
          />
          <Select
            allowClear
            placeholder="Status"
            className="gfx-oversight-select"
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={STATUS_OPTIONS.map((s) => ({ label: s, value: s }))}
          />
          <Select
            allowClear
            placeholder="Priority"
            className="gfx-oversight-select gfx-oversight-select--sm"
            onChange={(v) => setFilters((f) => ({ ...f, priority: v }))}
            options={PRIORITY_OPTIONS.map((p) => ({ label: p, value: p }))}
          />
        </div>
      </Card>

      <Card bordered={false} className="criclebox tablespace mb-24">
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={requests}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Card bordered={false} className="criclebox tablespace mb-24" title="Blog Write Team Workload (±30 days)">
        <Table rowKey="assignedTo" columns={workloadColumns} dataSource={workloadReport} pagination={{ pageSize: 10 }} />
      </Card>

      <Modal
        open={!!decisionModal}
        title="Request Revisions"
        onCancel={() => setDecisionModal(null)}
        onOk={() => decisionForm.submit()}
        okText="Request Revisions"
        confirmLoading={!!decidingId}
      >
        <Form form={decisionForm} layout="vertical" onFinish={submitDecision}>
          <Form.Item label="Comments" name="comment">
            <TextArea rows={3} placeholder="Add feedback for the Blog Write Team member..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!editingRecord}
        title="Edit Blog Write Request"
        width={720}
        onCancel={() => setEditingRecord(null)}
        okText="Save Changes"
        okButtonProps={{ loading: editSaving }}
        onOk={() => editForm.submit()}
      >
        {editingRecord?.status === "Completed" && (
          <Alert
            type="warning"
            showIcon
            message="This will reopen the request"
            description="It's currently Completed — saving changes here sends it back to the Blog Write Team member as an active request. Reassigning to a different member instead resets it to a fresh start."
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={editForm} layout="vertical" onFinish={submitEdit}>
          <Form.Item label="Blog Post Outline Framework">
            <div className="blog-desc-trigger">
              <Button onClick={() => setDescModalOpen(true)}>
                {outlineHasContent(editOutline) ? "Edit Blog Post Outline Framework" : "+ Add Blog Post Outline Framework"}
              </Button>
              {outlineHasContent(editOutline) && <Tag color="green">Blog Post Outline Framework added ✓</Tag>}
            </div>
          </Form.Item>
          <Row gutter={20}>
            <Col span={12}>
              <Form.Item label="Delivery Date" name="deliveryDate" rules={[{ required: true, message: "Required" }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Priority" name="priority">
                <Select options={["Low", "Medium", "High", "Urgent"].map((p) => ({ label: p, value: p }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Assign To" name="assignedTo">
            <Select
              allowClear
              placeholder="Blog Write Team member"
              options={members.map((m) => ({ label: m.name, value: m._id }))}
            />
          </Form.Item>
          {assignedToWatch !== editingOriginalAssignee && (
            <Form.Item
              label="Reason for Reassignment"
              name="reassignReason"
              rules={[{ required: true, message: "Please add a short reason — it will be shown in the audit log" }]}
            >
              <Input placeholder="e.g. Original assignee is on leave" />
            </Form.Item>
          )}
          <Form.Item label="Add Reference Files (optional)">
            <Upload
              multiple
              beforeUpload={(file) => {
                setEditFileList((prev) => [...prev, file]);
                return false;
              }}
              onRemove={(file) => setEditFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
              fileList={editFileList}
            >
              <Button icon={<UploadOutlined />}>Select Files</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <BlogDescriptionModal
        open={descModalOpen}
        value={editOutline}
        onChange={setEditOutline}
        onClose={() => setDescModalOpen(false)}
      />
    </>
  );
}

export default BlogWriteOversight;
